import React, { useEffect, useRef, useState } from "react";
import { Col, FormControl, Grid, Row, Table } from "react-bootstrap";
import NotificationSystem from "react-notification-system";
import Card from "components/Card/Card.jsx";
import ScreenLoader from "views/Components/screenLoader";
import Button from "../components/SurveyButton";
import SurveyActionMenu from "../components/SurveyActionMenu";
import SurveyConfirmationModal from "../components/SurveyConfirmationModal";
import SurveyPagination from "../components/SurveyPagination";
import { SurveyStatusBadge as Status } from "../components/SurveyStatusBadge";
import SurveyTestDeploymentModal from "../test-deployment/SurveyTestDeploymentModal";
import {
  apiErrorMessage,
  deleteSurvey,
  getSurvey,
  getSurveys,
  paginatorFromResponse,
  updateSurvey,
} from "api/survey/surveyApi";
import {
  duplicateSurveyForm,
  surveyToForm,
} from "../domain/survey.mappers";
import { formatDate } from "../domain/survey.formatters";
import { validatePublishableSurvey } from "../domain/survey.validation";
import "../styles/index.css";

const emptyPagination = {
  rows: [],
  currentPage: 1,
  lastPage: 1,
  perPage: 15,
  total: 0,
  from: 0,
  to: 0,
};

const SurveyListPage = ({ history }) => {
  const notificationRef = useRef(null);
  const confirmationBusyRef = useRef(false);
  const [surveys, setSurveys] = useState(emptyPagination);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [testDeploymentSurvey, setTestDeploymentSurvey] = useState(null);

  const notify = (message, level = "success") => {
    if (notificationRef.current)
      notificationRef.current.addNotification({
        message,
        level,
        position: "bl",
        autoDismiss: 5,
      });
  };

  const loadSurveys = async (page = 1, nextStatus = status) => {
    setLoading(true);
    try {
      setSurveys(paginatorFromResponse(await getSurveys(page, nextStatus)));
    } catch (error) {
      notify(apiErrorMessage(error, "Unable to load surveys."), "error");
    }
    setLoading(false);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    loadSurveys(1, status);
  }, [status]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const publish = async (survey) => {
    setLoading(true);
    try {
      const response = await getSurvey(survey.id);
      const validation = validatePublishableSurvey(surveyToForm(response.data));
      if (!validation.isValid) {
        notify(
          "Complete the survey details and questions before publishing.",
          "warning",
        );
        setLoading(false);
        history.push(`/admin/surveys/${survey.id}`);
        return;
      }
      setConfirmation({
        action: "publish",
        survey,
        title: "Publish Survey?",
        message: `Publish “${survey.title}”? Published surveys cannot be edited.`,
        confirmLabel: "Publish",
        confirmStyle: "success",
      });
    } catch (error) {
      notify(apiErrorMessage(error, "Unable to publish the survey."), "error");
    }
    setLoading(false);
  };

  const deployTest = async (survey) => {
    setLoading(true);
    try {
      const response = await getSurvey(survey.id);
      const validation = validatePublishableSurvey(surveyToForm(response.data));
      if (!validation.isValid) {
        notify(
          "Complete the survey details and questions before deploying it for testing.",
          "warning",
        );
        history.push(`/admin/surveys/${survey.id}`);
        return;
      }
      setTestDeploymentSurvey(survey);
    } catch (error) {
      notify(
        apiErrorMessage(
          error,
          "Unable to prepare the survey for test deployment.",
        ),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const inactivate = async (survey) => {
    setConfirmation({
      action: "inactivate",
      survey,
      title: "Inactivate Survey?",
      message: `Make “${survey.title}” inactive? Customers will no longer see it.`,
      confirmLabel: "Inactivate",
      confirmStyle: "warning",
    });
  };

  const duplicate = async (survey) => {
    setLoading(true);
    try {
      const response = await getSurvey(survey.id);
      const duplicateDraft = duplicateSurveyForm(surveyToForm(response.data));
      setLoading(false);
      history.push({
        pathname: "/admin/surveys/new",
        state: { duplicateDraft },
      });
    } catch (error) {
      notify(
        apiErrorMessage(error, "Unable to prepare the survey copy."),
        "error",
      );
      setLoading(false);
    }
  };

  const remove = async (survey) => {
    setConfirmation({
      action: "delete",
      survey,
      title: "Delete Survey?",
      message: `Delete “${survey.title}”? The survey will be hidden, but its historical submissions will remain stored.`,
      confirmLabel: "Delete",
      confirmStyle: "danger",
    });
  };

  const confirmAction = async () => {
    if (!confirmation || confirmationBusyRef.current) return;
    const { action, survey } = confirmation;
    confirmationBusyRef.current = true;
    setConfirming(true);
    setLoading(true);
    try {
      if (action === "publish") {
        await updateSurvey(survey.id, { status: "published" });
        notify("Survey published successfully.");
        await loadSurveys(surveys.currentPage);
      } else if (action === "inactivate") {
        await updateSurvey(survey.id, { status: "inactive" });
        notify("Survey made inactive successfully.");
        await loadSurveys(surveys.currentPage);
      } else if (action === "delete") {
        await deleteSurvey(survey.id);
        notify("Survey deleted successfully.");
        const nextPage =
          surveys.rows.length === 1 && surveys.currentPage > 1
            ? surveys.currentPage - 1
            : surveys.currentPage;
        await loadSurveys(nextPage);
      }
      setConfirmation(null);
    } catch (error) {
      const fallback =
        action === "publish"
          ? "Unable to publish the survey."
          : action === "inactivate"
            ? "Unable to inactivate the survey."
            : "Unable to delete the survey.";
      notify(apiErrorMessage(error, fallback), "error");
    }
    confirmationBusyRef.current = false;
    setConfirming(false);
    setLoading(false);
  };

  const openSurvey = (survey, submissions = false) => {
    history.push(
      `/admin/surveys/${survey.id}${submissions ? "?tab=submissions" : ""}`,
    );
  };

  const secondaryActions = (survey) => [
    {
      key: "view",
      label: survey.status === "draft" ? "View / Edit" : "View",
      icon: survey.status === "draft" ? "fa-pencil" : "fa-eye",
      onSelect: () => openSurvey(survey),
    },
    survey.status === "draft" && {
      key: "deploy",
      label: "Deploy Test",
      icon: "fa-flask",
      onSelect: () => deployTest(survey),
    },
    (survey.status === "published" || survey.status === "inactive") && {
      key: "submissions",
      label: "Submissions",
      icon: "fa-list-alt",
      onSelect: () => openSurvey(survey, true),
    },
    {
      key: "duplicate",
      label: "Duplicate",
      icon: "fa-copy",
      onSelect: () => duplicate(survey),
    },
    {
      key: "delete",
      label: "Delete",
      icon: "fa-trash",
      danger: true,
      dividerBefore: true,
      onSelect: () => remove(survey),
    },
  ];

  return (
    <div className="main-content survey-engine">
      {loading && <ScreenLoader />}
      <NotificationSystem ref={notificationRef} />
      <Grid fluid>
        <Row>
          <Col md={12}>
            <div className="survey-engine-list-card">
            <Card
              title="Surveys"
              category="Create, publish, and review customer surveys."
              content={
                <>
                  <div className="survey-engine-toolbar">
                    <div className="survey-engine-filter">
                      <label htmlFor="survey-status-filter">Status</label>
                      <FormControl
                        id="survey-status-filter"
                        componentClass="select"
                        value={status}
                        onChange={(event) => setStatus(event.target.value)}
                      >
                        <option value="">All statuses</option>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="inactive">Inactive</option>
                      </FormControl>
                    </div>
                    <Button
                      bsStyle="primary"
                      icon="fa-plus"
                      solid
                      onClick={() => history.push("/admin/surveys/new")}
                    >
                      Create Survey
                    </Button>
                  </div>
                  <Table
                    responsive
                    striped
                    hover
                    className="survey-engine-table"
                  >
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Questions</th>
                        <th>Submissions</th>
                        <th>Published</th>
                        <th>Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && !surveys.rows.length && (
                        <tr>
                          <td colSpan="7" className="survey-engine-empty">
                            No surveys found.
                          </td>
                        </tr>
                      )}
                      {surveys.rows.map((survey) => (
                        <tr
                          key={survey.id}
                          role="link"
                          tabIndex={0}
                          onClick={() => openSurvey(survey)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openSurvey(survey);
                            }
                          }}
                        >
                          <td>
                            <strong>{survey.title}</strong>
                            <small>#{survey.id}</small>
                          </td>
                          <td>
                            <Status value={survey.status} />
                          </td>
                          <td>{survey.questionCount}</td>
                          <td>{survey.submissionCount}</td>
                          <td>{formatDate(survey.publishedAt)}</td>
                          <td>{formatDate(survey.updatedAt)}</td>
                          <td
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                          >
                            <div className="survey-engine-actions">
                              {survey.status === "draft" && (
                                <Button
                                  bsSize="small"
                                  bsStyle="success"
                                  icon="fa-paper-plane"
                                  onClick={() => publish(survey)}
                                >
                                  Publish
                                </Button>
                              )}
                              {survey.status === "published" && (
                                <Button
                                  bsSize="small"
                                  bsStyle="warning"
                                  icon="fa-ban"
                                  onClick={() => inactivate(survey)}
                                >
                                  Inactivate
                                </Button>
                              )}
                              <SurveyActionMenu
                                id={`survey-actions-${survey.id}`}
                                actions={secondaryActions(survey)}
                                disabled={loading}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <SurveyPagination
                    pagination={surveys}
                    onPage={(page) => loadSurveys(page)}
                    loading={loading}
                  />
                </>
              }
            />
            </div>
          </Col>
        </Row>
      </Grid>
      <SurveyConfirmationModal
        show={Boolean(confirmation)}
        title={confirmation && confirmation.title}
        message={confirmation && confirmation.message}
        confirmLabel={confirmation && confirmation.confirmLabel}
        confirmStyle={confirmation && confirmation.confirmStyle}
        busy={confirming}
        onCancel={() => setConfirmation(null)}
        onConfirm={confirmAction}
      />
      <SurveyTestDeploymentModal
        show={Boolean(testDeploymentSurvey)}
        surveyId={testDeploymentSurvey && testDeploymentSurvey.id}
        onHide={() => setTestDeploymentSurvey(null)}
        onDeployed={(result) => {
          notify(
            `${result.assignedCount} lead${result.assignedCount === 1 ? "" : "s"} assigned for testing.`,
          );
        }}
      />
    </div>
  );
};

export default SurveyListPage;
